import { useCallback, useEffect, useRef } from 'react';

// Each conversation and topic owns a draft, including pending object URLs.
export default function useChatTopicComposerDrafts({ conversationKey, text, pendingImages, selectedMentions, replyDraft, topics, onReplyChange, onReplyCancel, setText, setPendingImages, setSelectedMentions, setMentionState, setMentionActiveTab, clearPendingImages, pendingImagesRef, revokePendingImageEntries }: Record<string, any>) {
  const currentConversationKeyRef = useRef(conversationKey);
  currentConversationKeyRef.current = conversationKey;
  const previousConversationKeyRef = useRef(conversationKey);
  const draftsRef = useRef<Map<string, any>>(new Map());
  const latestDraftRef = useRef<any>(null);
  latestDraftRef.current = { text, pendingImages, selectedMentions, replyDraft };

  useEffect(() => {
    if (previousConversationKeyRef.current === conversationKey) return;
    draftsRef.current.set(previousConversationKeyRef.current, latestDraftRef.current);
    previousConversationKeyRef.current = conversationKey;
    const saved = draftsRef.current.get(conversationKey);
    setText(saved?.text || "");
    setPendingImages(saved?.pendingImages || []);
    setSelectedMentions(saved?.selectedMentions || []);
    if (onReplyChange) onReplyChange(topics?.pendingReplyDraft || saved?.replyDraft || null);
    else onReplyCancel?.();
    if (topics) topics.pendingReplyDraft = null;
    setMentionState(null);
    setMentionActiveTab("person");
  }, [clearPendingImages, conversationKey]);

  useEffect(() => () => {
    revokePendingImageEntries(pendingImagesRef.current);
    draftsRef.current.forEach((draft) => revokePendingImageEntries(draft?.pendingImages || []));
  }, [revokePendingImageEntries]);

  const clearSendingDraft = useCallback(() => {
    if (currentConversationKeyRef.current === conversationKey) {
      setText(""); setSelectedMentions([]); setMentionState(null);
    } else {
      const saved = draftsRef.current.get(conversationKey) || {};
      draftsRef.current.set(conversationKey, { ...saved, text: "", selectedMentions: [] });
    }
  }, [conversationKey]);
  const restoreSendingDraft = useCallback((pendingText: any) => {
    if (currentConversationKeyRef.current === conversationKey) {
      setText((currentText: string) => currentText || pendingText.value);
      setSelectedMentions((mentions: any[]) => mentions.length ? mentions : pendingText.mentions);
    } else {
      const saved = draftsRef.current.get(conversationKey) || {};
      draftsRef.current.set(conversationKey, { ...saved, text: saved.text || pendingText.value, selectedMentions: saved.selectedMentions?.length ? saved.selectedMentions : pendingText.mentions });
    }
  }, [conversationKey]);
  const clearSentImages = useCallback(() => {
    if (currentConversationKeyRef.current === conversationKey) clearPendingImages();
    else {
      const saved = draftsRef.current.get(conversationKey) || {};
      revokePendingImageEntries(saved.pendingImages || []);
      draftsRef.current.set(conversationKey, { ...saved, pendingImages: [] });
    }
  }, [clearPendingImages, conversationKey, revokePendingImageEntries]);

  return { clearSendingDraft, restoreSendingDraft, clearSentImages };
}
